/* ---------------------------------------------------------------------------
   The register: every structure on file, one row each.

   Searching happens in the application header — it is the one search in the app
   and it searches this table, so it belongs where the eye already goes rather
   than on a screen you have to reach first. What is left here is what only makes
   sense over a table: which district, which road, and how tight the rows.

   On a desk this is the start page's first panel (registerPanel); on a phone it
   is the Bridges tab (fieldRegister).
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, can, roleCan, store, type State } from '../core/store.ts';
import { button, chip, emptyState, tag } from '../components/widgets.ts';
import { panel } from '../components/panel.ts';
import { flyout, menuButton, menuItem, menuSep } from '../components/flyout.ts';
import { openBridgeIn } from '../core/navigate.ts';
import { closestView } from '../map/nepalMap.ts';
import {
  allBridges,
  cycleLabel,
  districtNames,
  inspectionStanding,
  prettyDate,
  standingLabel,
} from '../data/derive.ts';
import type { BridgeView } from '../data/types.ts';
import {
  count,
  isRecorded,
  join,
  NOT_RECORDED,
  shown,
} from '../data/recorded.ts';
import { vocab } from '../tenant/agency.ts';
import { phaseChip, worksChip } from '../components/phaseChip.ts';
import { statusChip } from '../components/statusChip.ts';
import { operationalStatus } from '../data/posting.ts';
import { phaseOf, stateKey } from '../data/phase.ts';
import { isOpenStatus } from '../data/defects.ts';
import { chainage } from '../tenant/format.ts';

/**
 * The "no district filter" sentinel — a chip label, not a district.
 *
 * Exported because the URL has to know it: src/core/router.ts leaves the
 * district out of the address entirely when this is what is in force, so a
 * shared register link says only what is actually filtered.
 */
export const ALL_DISTRICTS = 'All districts';
/** The road filter's "no filter", as `ALL_DISTRICTS` is the district's. */
export const ALL_ROADS = 'All roads';

/* --------------------------- filtering / sorting --------------------------- */

/**
 * Every column the table can be sorted by, in the order COLS below lists them.
 *
 * A list rather than a bare union because the URL carries the sort key, and a
 * key arriving from an address bar has to be checked against something at
 * runtime — see src/core/router.ts. Keep it in step with COLS: the routing
 * checks in scripts/smoke.ts click every column heading the table renders and
 * assert the key each one sorts by is a key the address can name.
 */
export const SORT_KEYS = [
  'id',
  'name',
  'km',
  'district',
  'year',
  'length',
  'standing',
] as const;

export type SortKey = (typeof SORT_KEYS)[number];

function compare(a: BridgeView, b: BridgeView, key: SortKey): number {
  /* Derived rather than stored, and needs a rule for the structures never
     inspected: they sort to the end ascending, so "worst first" never means
     "unknown first". */
  if (key === 'standing') {
    // The worse of a structure's two cycles, so one structure sorts to one place.
    const da = inspectionStanding(a).worst.due;
    const db = inspectionStanding(b).worst.due;
    if (da === null) return db === null ? 0 : 1;
    if (db === null) return -1;
    return da < db ? -1 : da > db ? 1 : 0;
  }

  const va = a[key];
  const vb = b[key];
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb));
}

/** The rows the user is currently looking at, honouring every filter and the sort. */
export function visibleRegister(): BridgeView[] {
  const s = store.get();
  const q = s.q.trim().toLowerCase();

  const rows = allBridges().filter(
    (b) =>
      (s.district === ALL_DISTRICTS || b.district === s.district) &&
      (s.road === ALL_ROADS || b.route === s.road) &&
      (!q ||
        /* The haystack, with the unrecorded fields left out rather than
           stringified. A template would put the literal "null" in it for every
           field a record does not carry — so searching "null" would match most
           of the DPWH register, and a search for a real value would still work,
           which is what makes that kind of bug survive. */
        [b.id, b.name, b.route, b.roadName, b.river, b.district, b.type]
          .filter(isRecorded)
          .join(' ')
          .toLowerCase()
          .includes(q)),
  );

  const dir = s.sortDir === 'asc' ? 1 : -1;
  rows.sort((a, b) => compare(a, b, s.sortKey as SortKey) * dir);
  return rows;
}

/**
 * How many rows the table will draw at once.
 *
 * The register renders every row it is given, and at national scale that is the
 * largest single cost in the application: measured at 36,000 DOM nodes for 3,000
 * records and 120,000 for 10,000 (`npm run scale`). The work is linear — there
 * is no quadratic cliff hiding in the filter or the sort, both of which stay
 * under 15 ms at 10,000 records — it is simply a lot of nodes.
 *
 * So this caps what is DRAWN and says so, rather than pretending. Nothing is
 * hidden from the filters: the search and the district chips run over the whole
 * register and the count above the table is the true one. Narrowing the filter
 * is the intended answer, and at any register this project has today the cap is
 * never reached.
 *
 * The real fix is virtual scrolling, which would draw a screenful whatever the
 * count. It is not here because it cannot be verified without a browser, and a
 * scroller that is subtly wrong is worse than a cap that is honest.
 */
export const MAX_ROWS = 500;

/* --------------------------- columns --------------------------- */

interface Col {
  key: SortKey;
  label: string;
  align: 'left' | 'right';
  width?: string;
}

const COLS: Col[] = [
  { key: 'id', label: 'Asset id', align: 'left', width: '10%' },
  { key: 'name', label: 'Structure', align: 'left', width: '17%' },
  { key: 'km', label: 'Route / km', align: 'left', width: '10%' },
  { key: 'district', label: 'District', align: 'left', width: '17%' },
  { key: 'length', label: 'Length', align: 'right', width: '9%' },
  { key: 'year', label: 'Built', align: 'right', width: '8%' },
  { key: 'standing', label: 'Inspection', align: 'left', width: '23%' },
];

/** Row heights offered in the register's header. */
const DENSITIES = [
  { word: 'Compact', px: 28, title: 'Compact rows' },
  { word: 'Balanced', px: 34, title: 'Balanced rows' },
  { word: 'Airy', px: 42, title: 'Airy rows' },
] as const;

/** The chip to light up for a persisted height that is not exactly one of ours. */
function nearestDensity(px: number): number {
  return DENSITIES.reduce<number>(
    (best, d) => (Math.abs(d.px - px) < Math.abs(best - px) ? d.px : best),
    DENSITIES[0].px,
  );
}

/* --------------------------- screen --------------------------- */

/* --------------------------- the field list ---------------------------

   The Bridges tab. Cards, not the table — and a tap that OPENS the record.

   BOTH OF THOSE ARE DEPARTURES FROM THE DESK REGISTER, and both were written
   into FIELD.md before they were written into code, which is why the field shell
   shipped for a while showing a fourteen-column sortable table on a phone. The
   table is not a bad phone layout by accident; it is a good desk layout, and it
   carries a density control, a split/grid switch, a CSV export, a bulk import and
   sortable headings, none of which belong in a hand at a riverbank.

   THE TAP IS THE OTHER HALF. On the desk register a row deliberately only
   SELECTS — the note on its row handler below says why: there is a preview pane
   beside it, and browsing the table should not navigate away. There is no preview
   pane here and nothing else to do with a selection, so a row that did not open
   the record would be a tap that does nothing, which is exactly how it read.

   What a card carries is what an inspector needs to pick the right structure:
   its name, enough of an address to know it is the one, whether it is due, how
   many defects are open, and when it was last seen. Nothing else. Every other
   column the desk table has is on the record, one tap away.
   --------------------------------------------------------------------------- */

/** How many cards to draw. Well past a district; the search narrows the rest. */
const MAX_CARDS = 60;

/**
 * One structure, as a card.
 *
 * A `<button>` rather than a div with a click handler: it is a control that
 * navigates, so it should be reachable by keyboard and announced as one — and on
 * a phone the whole card being the target is what makes it easy to hit.
 */
function bridgeCard(b: BridgeView, s: Readonly<State>): HTMLElement {
  const standing = inspectionStanding(b);
  const open = s.registerDefects.filter((d) => d.bridgeId === b.id && isOpenStatus(d.status));
  const worst = standing.worst;

  return h(
    'button',
    {
      type: 'button',
      class: ['fcard', b.id === s.selectedId && 'fcard--on'],
      title: standingLabel(standing),
      onclick: () => actions.openBridge(b.id),
    },
    h(
      'span',
      { class: 'fcard__top' },
      h('span', { class: 'fcard__name', text: b.name }),
      /* Only where it is not plain in service: a phone's card has room for one
         thing that is unusual, not a chip saying everything is ordinary. */
      stateKey(phaseOf(b)) === 'in-service' ? null : phaseChip(b, true),
      worksChip(b),
      /* Posted or closed, where it is (LC-23): open is the ordinary state. */
      operationalStatus(b).kind === 'posted' ? statusChip(b) : null,
      /* The due state, on the accent ramp like every other rank in this
         application — `worst` is whichever of the two cycles is in the worse
         state, because a principal inspection six years late must not hide
         behind a routine one done last month. */
      tag(worst.status, worst.rank, cycleLabel(worst)),
    ),
    h('span', {
      class: 'fcard__sub',
      text: join([b.id, b.route, chainage(b.km)], ' · '),
    }),
    h(
      'span',
      { class: 'fcard__facts' },
      fieldFact('Crosses', shown(b.river)),
      fieldFact('Inspection', open.length ? `${open.length} open` : 'None'),
      fieldFact('Last seen', standing.latest ? prettyDate(standing.latest.date) : 'Never'),
    ),
  );
}

function fieldFact(label: string, value: string): HTMLElement {
  return h(
    'span',
    { class: 'fcard__fact' },
    h('span', { class: 'fcard__fact-k', text: label }),
    h('span', { class: 'fcard__fact-v', text: value }),
  );
}

function fieldRegister(s: Readonly<State>): HTMLElement {
  const all = allBridges();
  const matched = visibleRegister();
  const rows = matched.slice(0, MAX_CARDS);

  return h(
    'div',
    { class: 'flist' },

    /* SEARCH AND DISTRICTS ARE DOCKED, not scrolled. They are how you find one
       structure out of a national register, and a filter that leaves the screen
       as soon as you start looking is a filter you have to scroll back up to
       change. Sticky rather than moved out of the scroller: the screen renders
       inside `<main class="scroll">` (see src/main.ts) and hoisting a control out
       of that would mean the shell laying out something only one screen has.

       Same pattern as the record's pinned action bar — see .frec__actions. */
    h(
      'div',
      { class: 'flist__dock' },

    /* Search first, because on a phone it is faster than scrolling past four
       districts. `id` because src/main.ts restores focus and caret across a
       re-render by id, and this field is typed into on every keystroke. */
    h(
      'label',
      { class: 'flist__search', for: 'flist-q' },
      icon(ICONS.search, { size: 20, width: 1.8, stroke: 'var(--txt3)' }),
      h('input', {
        id: 'flist-q',
        class: 'flist__input',
        type: 'search',
        value: s.q,
        placeholder: 'Search name, id or river',
        oninput: (e: Event) => store.set({ q: (e.target as HTMLInputElement).value }),
      }),
    ),

    /* Districts only. The desk toolbar's other controls — density, split/grid,
       import, CSV — are desk work, and a phone that offered a bulk CSV import
       would be offering it to somebody standing in a river. */
    h(
      'div',
      { class: 'flist__chips' },
      ...[ALL_DISTRICTS, ...districtNames(all)].map((d) =>
        chip(d === ALL_DISTRICTS ? 'All' : d, d === s.district, () => store.set({ district: d })),
      ),
    ),
    ),

    rows.length
      ? h('div', { class: 'flist__cards' }, ...rows.map((b) => bridgeCard(b, s)))
      : h('div', {
          class: 'flist__none',
          /* Two different nothings. A filter that matches nothing is not the
             register being empty, and an unreachable backend is neither. */
          text:
            s.backend !== 'live' && !all.length
              ? 'The register could not be read, so this is not a claim that there are no structures.'
              : 'No structure matches that search.',
        }),

    matched.length > rows.length
      ? h('div', {
          class: 'flist__note',
          text: `${rows.length} of ${matched.length} matching shown — narrow the search to see the rest.`,
        })
      : null,

    h('div', {
      class: 'flist__note',
      text: 'Positions are map pins, not surveyed points — chainage is the location of record.',
    }),
  );
}

export function registerScreen(s: Readonly<State>): HTMLElement {
  if (s.shell === 'field') return fieldRegister(s);
  /* The desk does not route here — its register is the start page (see
     deskSendsHome) — but a caller that draws this screen gets the same panel
     the start page does rather than a second, drifting table. */
  return registerPanel(s);
}

/* --------------------------- the desk register ---------------------------

   The start page's first panel: every structure on file, a row each, with the
   filters in the panel's header. NO PREVIEW PANE. Everything about one structure
   is in the applications, so a row opens its record in Bridge and the
   last column opens it in any of the four — the register says which structures
   there are and how they stand, and stops there.
   --------------------------------------------------------------------------- */

/** One of a filter's choices, with how many structures it leaves. */
interface Choice {
  value: string;
  label: string;
  count: number;
}

function districtChoices(all: BridgeView[]): Choice[] {
  return [
    { value: ALL_DISTRICTS, label: ALL_DISTRICTS, count: all.length },
    ...districtNames(all).map((d) => ({ value: d, label: d, count: all.filter((b) => b.district === d).length })),
  ];
}

/**
 * The carrying roads, busiest first, and the structures whose road is not on
 * file as a choice of their own — so the counts add up to the register.
 */
function roadChoices(all: readonly BridgeView[]): Choice[] {
  const counts = new Map<string, number>();
  for (const b of all) if (b.route !== null && isRecorded(b.route)) counts.set(b.route, (counts.get(b.route) ?? 0) + 1);
  const roads = [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return [
    { value: ALL_ROADS, label: ALL_ROADS, count: all.length },
    ...roads.map(([road, n]) => ({ value: road, label: road, count: n })),
  ];
}

/** A register filter: a button naming what is in force, and the list under it. */
function filterMenu(
  s: Readonly<State>,
  id: 'district' | 'road',
  caption: string,
  current: string,
  all: string,
  choices: readonly Choice[],
  choose: (value: string) => void,
): HTMLElement[] {
  const button = h(
    'button',
    { type: 'button', class: 'ddbtn', title: `Filter the register by ${caption.toLowerCase()}`, ...menuButton(s, id) },
    h('span', { class: 'ddbtn__cap', text: caption }),
    h('span', { text: current === all ? 'All' : current }),
    icon(ICONS.chevronDown, { size: 11, width: 2 }),
  );
  const list = flyout(
    s,
    id,
    { label: `${caption} filter`, width: 248, align: 'start' },
    h(
      'div',
      { class: 'flyout__list' },
      ...choices.map((c, i) => [
        menuItem({
          label: c.label,
          aside: String(c.count),
          checked: c.value === current,
          onClick: () => choose(c.value),
        }),
        i === 0 && choices.length > 1 ? menuSep() : null,
      ]),
    ),
  );
  return list ? [button, list] : [button];
}

export function registerPanel(s: Readonly<State>): HTMLElement {
  /* Whoever keeps the inventory ticks rows for the bulk record (LC-80). */
  const mayRecord = roleCan(s, 'inventory.edit') && s.backend === 'live';
  const all = allBridges();
  const matched = visibleRegister();
  const rows = matched.slice(0, MAX_ROWS);
  const density = nearestDensity(s.rowHeight);
  const countLabel =
    matched.length > rows.length
      ? `${rows.length} shown of ${matched.length} · ${all.length} on file`
      : matched.length === all.length
        ? String(all.length)
        : `${matched.length} of ${all.length}`;

  /* ---------- the header's filters ---------- */
  const filters = [
    ...filterMenu(s, 'district', vocab().area, s.district, ALL_DISTRICTS, districtChoices(all), (d) =>
      store.set({ district: d }),
    ),
    ...filterMenu(s, 'road', 'Road', s.road, ALL_ROADS, roadChoices(all), (r) => store.set({ road: r })),
    h('span', { class: 'ribbon__sep', 'aria-hidden': 'true' }),
    h('span', { class: 'ddbtn__cap', text: 'Rows' }),
    h(
      'div',
      { class: 'seg', role: 'group', 'aria-label': 'Row height' },
      ...DENSITIES.map((d) =>
        h(
          'button',
          {
            type: 'button',
            class: ['seg__opt', density === d.px && 'seg__opt--on'],
            title: d.title,
            'aria-pressed': String(density === d.px),
            onclick: () => actions.setRowHeight(d.px),
          },
          d.word,
        ),
      ),
    ),
    /* `inventory.import`. Absent rather than disabled: an import rewrites rows
       across the whole register, it is the widest-reaching write this
       application has, and somebody who may not perform it has no use for a
       greyed control beside the filters. */
    can(s, 'inventory.import')
      ? button('Import', {
          sm: true,
          iconPath: ICONS.import,
          title: 'Bring inventory rows in from a CSV, with a report before anything is written',
          onClick: () => actions.openImport(),
        })
      : null,
    /* LC-80: the ticked rows, already built, recorded as in service. */
    mayRecord
      ? button(s.registerPicked.length ? `Record in service · ${s.registerPicked.length}` : 'Record in service', {
          sm: true,
          iconPath: ICONS.check,
          disabled: s.registerPicked.length === 0 || s.phaseBusy,
          title: s.registerPicked.length
            ? 'Record each ticked structure as in service, from its year of completion; those already recorded are skipped'
            : 'Tick the structures already built to record them as in service',
          onClick: () => void actions.recordPickedInService(),
        })
      : null,
  ];

  /* ---------- the table ---------- */
  const head = h(
    'thead',
    {},
    h(
      'tr',
      {},
      ...COLS.map((c) => {
        const active = s.sortKey === c.key;
        return h(
          'th',
          {
            class: c.align === 'right' ? 'r' : '',
            ...(c.width ? { style: `width:${c.width}` } : {}),
            'aria-sort': active ? (s.sortDir === 'asc' ? 'ascending' : 'descending') : 'none',
            onclick: () => actions.sort(c.key),
          },
          c.label,
          active ? h('span', { class: 'arrow', text: s.sortDir === 'asc' ? '↑' : '↓' }) : null,
        );
      }),
      h('th', { class: 'r', style: 'width:6%', text: 'Map' }),
    ),
  );

  const body = h(
    'tbody',
    {},
    ...rows.map((b) => {
      const standing = inspectionStanding(b);
      return h(
        'tr',
        {
          class: b.id === s.selectedId ? 'on' : '',
          style: { height: `${s.rowHeight}px` },
          tabindex: '0',
          title: `Open ${b.name} in Bridge`,
          /* A row opens the structure's record in Bridge, on Bridge info — what
             the structure is, before any application's view of it. The last
             cell shows it in the map below. */
          onclick: () => openBridgeIn('structure', b.id),
          onkeydown: (e: Event) => {
            const ke = e as KeyboardEvent;
            if (ke.target !== ke.currentTarget) return;
            if (ke.key === 'Enter' || ke.key === ' ') {
              e.preventDefault();
              openBridgeIn('structure', b.id);
            }
          },
        },
        h(
          'td',
          { class: 'num nowrap' },
          mayRecord
            ? h('input', {
                type: 'checkbox',
                class: 'reg__pick',
                checked: s.registerPicked.includes(b.id),
                'aria-label': `Tick ${b.name}`,
                onclick: (e: Event) => e.stopPropagation(),
                onchange: () => actions.togglePicked(b.id),
              })
            : null,
          b.id,
          b.inventoryCode
            ? h('span', {
                title: `Full inventory sheet on file — ${b.inventoryCode}`,
                style: 'color:var(--accent);margin-left:4px',
                text: '•',
              })
            : null,
        ),
        h('td', {
          class: 'ellipsis',
          style: 'max-width:200px;font-weight:600',
          title: join([b.name, b.river], ' — '),
          text: b.name,
        }),
        h('td', { class: 'nowrap num', style: 'color:var(--txt2)', title: b.roadName, text: b.routeKm }),
        h('td', { class: 'ellipsis', style: 'color:var(--txt2)', text: shown(b.district) }),
        h('td', { class: 'r num', text: b.length == null ? NOT_RECORDED : b.length.toFixed(2) }),
        h('td', { class: 'r num', style: 'color:var(--txt2)', text: count(b.year) }),
        h(
          'td',
          { class: 'reg__state' },
          /* Where the structure is in its life (LC-10), whether it may be
             crossed and at what weight (LC-23), then one chip for the inspection
             cycle in the worse state; the titles carry who decided and when. */
          phaseChip(b, true),
          worksChip(b),
          statusChip(b),
          tag(standing.worst.status, standing.worst.rank, standingLabel(standing)),
        ),
        h('td', { class: 'r' }, showOnMap(b)),
      );
    }),
  );

  const sortedBy = COLS.find((c) => c.key === s.sortKey)?.label ?? 'asset id';

  return panel(
    { title: 'Register', meta: countLabel, class: 'home-reg', actions: filters },
    rows.length
      ? h('div', { class: 'reg-scroll' }, h('table', { class: 'reg reg--home' }, head, body))
      : emptyState(
          s.backend !== 'live' && !all.length
            ? 'The register could not be read, so this is not a claim that there are no structures.'
            : 'No structure matches these filters.',
        ),
    h(
      'div',
      { class: 'reg__foot' },
      h('span', { text: `Sorted by ${sortedBy.toLowerCase()}` }),
      s.registerNote ? h('span', { class: 'reg__note', text: s.registerNote }) : null,
      h('span', { class: 'spacer' }),
      h('span', {
        text: 'Length in metres.',
      }),
    ),
  );
}

/**
 * The structure on the Network panel's map below the register: zoomed to it,
 * selected, and scrolled into view. Goes through focusBridgeOnMap, not a bare
 * write, so the selection moves the way every other selection does.
 */
function showOnMap(b: BridgeView): HTMLElement {
  const { lat, lng } = b;
  const placed = lat != null && lng != null;
  return h(
    'button',
    {
      type: 'button',
      class: 'openin__btn',
      disabled: !placed,
      title: placed ? `Show ${b.name} in the map` : `${b.name} has no position on record`,
      'aria-label': `Show ${b.name} in the map`,
      onclick: (e: Event) => {
        /* The row underneath opens Bridge, so it must not also run. */
        e.stopPropagation();
        if (lat == null || lng == null) return;
        actions.focusBridgeOnMap(b.id, closestView({ lat, lng }, store.get().mapBase));
        document.querySelector('.home-net')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    },
    icon(ICONS.map, { size: 15 }),
  );
}
